/**
 * Haptic feedback utility for mobile devices
 * Provides consistent haptic feedback across the app
 */

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

/**
 * Trigger haptic feedback on supported devices
 * @param style - The type of haptic feedback to trigger
 */
export const triggerHaptic = (style: HapticStyle = 'light'): void => {
  // Check if the Vibration API is supported
  if (!('vibrate' in navigator)) {
    return;
  }

  // Define vibration patterns for different styles
  const patterns: Record<HapticStyle, number | number[]> = {
    light: 10,
    medium: 20,
    heavy: 30,
    success: [10, 50, 10], // short-pause-short
    warning: [20, 20, 20], // medium pulses
    error: [30, 10, 30, 10, 30], // strong rapid pulses
  };

  try {
    navigator.vibrate(patterns[style]);
  } catch (error) {
    // Silently fail if vibration is not allowed
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Check if haptic feedback is supported
 * @returns boolean indicating if haptic feedback is available
 */
export const isHapticSupported = (): boolean => {
  return 'vibrate' in navigator;
};

/**
 * Higher-level haptic feedback functions for specific interactions
 */
export const hapticFeedback = {
  // Button taps
  buttonTap: () => triggerHaptic('light'),
  
  // Play/Stop audio
  audioStart: () => triggerHaptic('medium'),
  audioStop: () => triggerHaptic('light'),
  
  // Question answers
  answerQuestion: () => triggerHaptic('medium'),
  
  // Success states
  testComplete: () => triggerHaptic('success'),
  
  // Error states
  error: () => triggerHaptic('error'),
  
  // Slider interactions
  sliderChange: () => triggerHaptic('light'),
  
  // Toggle switches
  toggle: () => triggerHaptic('light'),
};